import { Component, Input, ViewChild, inject } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData,
} from '../call-logs-query-editor-keyword.component';
import {
  ZiroSelectComponent,
  ZiroSelectConfig,
  ZiroSelectOption,
} from '../../../../forms/fields/select/ziro-select.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhoneResult } from '../../../models/generated/ziroModels';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { SearchPhoneResource } from '../../../resources/search-phone.resource';

@Component({
  selector: 'ziro-expression-editor-keyword-device-search',
  templateUrl: './expression-editor-keyword-device-search.component.html',
  standalone: false,
})
export class ExpressionEditorKeywordDeviceSearchComponent extends ZiroFormAbstractDirective<
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData
> {
  protected _ziroFormStateService: ZiroFormStateService;
  private searchPhoneResource = inject(SearchPhoneResource);

  // TODO: Skipped for migration because:
  //  This input is inherited from a superclass, but the parent cannot be migrated.
  @Input() entity: ExpressionEditorKeywordSearchEntity;
  @ViewChild(ZiroSelectComponent) selectField: ZiroSelectComponent;

  formConfig = {
    fields: {
      'keyword-search': {
        dataAutomation: 'device-select',
        componentConfig: new ZiroSelectConfig({
          asyncOptionsFn: this.searchDevice.bind(this),
          placeholder: 'tkey;reports.call_logs.keyword.search.device',
          clearWithInput: true,
          hideDropdownArrow: true,
          minSearchLength: 3,
          hideClear: true,
          keepSearchInput: true,
        }),
      },
    },
    options: {
      columnClasses: {
        label: 'd-none',
        input: 'col-12',
      },
    },
  } as ZiroFormConfig;

  searchDevice(q: string): Observable<ZiroSelectOption[]> {
    return this.searchPhoneResource
      .get({
        q,
      })
      .pipe(
        map((results: PhoneResult[]) => {
          const phoneResults: ZiroSelectOption[] = results.map((result: PhoneResult) => {
            return {
              label: result.ref.name,
              value: result.ref.name,
            };
          });
          const uniquePhoneResults: ZiroSelectOption[] = Array.from(
            new Map(phoneResults.map((phoneResult: ZiroSelectOption) => [phoneResult.label, phoneResult])).values(),
          );
          return uniquePhoneResults.sort(
            (result1: { label: string; value: string }, result2: { label: string; value: string }) => {
              return result1.label.localeCompare(result2.label);
            },
          );
        }),
      );
  }

  protected submit(): Observable<any> {
    return null;
  }

  toFormData = (entity: ExpressionEditorKeywordSearchEntity): ExpressionEditorKeywordSearchFormData => {
    return {
      'keyword-search': {
        label: entity.extensionUsernameOrDeviceName.value,
        value: entity.extensionUsernameOrDeviceName.value,
      },
    } as ExpressionEditorKeywordSearchFormData;
  };

  toEntity = (formData: ExpressionEditorKeywordSearchFormData): ExpressionEditorKeywordSearchEntity => {
    return {
      extensionUsernameOrDeviceName: { selection: 'device', value: formData['keyword-search'].value },
    } as ExpressionEditorKeywordSearchEntity;
  };

  handleSelectClosed(): void {
    if (typeof this.selectField.fieldData === 'string' && this.selectField.fieldData.length >= 3) {
      // Nothing was selected but we received a search term and we'd like to use it.
      this.ziroFormsUpdateSource.next({
        valid: ZiroFormsValidationState.VALID,
        old: {
          ...this.entity,
        },
        new: {
          extensionUsernameOrDeviceName: {
            selection: 'device',
            value: this.selectField.fieldData,
          },
        },
      });
    }
  }
}
